import React, { useEffect, useState } from "react";
import RegisterHeader from "./Common/Header";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Container,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useRegister } from "../../context/RegisterContext";
import { useNavigate } from "react-router-dom";
import { REQUIRED_FIELD, EMAIL_FIELD } from "../../utils/constants";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  RegisterData,
  RegisterPayload,
  SignUp,
} from "../../types/RegisterType";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Footer from "../Footer";
import ReactGA from "react-ga4";

type AlertType = {
  type: "success" | "error" | "info" | "warning";
  message: string;
};

const RegisterSignUp = () => {
  const navigate = useNavigate();
  const { registerSignup, getRegisterData, canProceed } = useRegister();
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertType | null>(null);
  const [open, setOpen] = React.useState(false);

  const schemaSignup = yup.object({
    email: yup
      .string()
      .required(REQUIRED_FIELD)
      .email(EMAIL_FIELD)
      .transform((value) => value?.toLowerCase().trim()),
    emailConfirm: yup
      .string()
      .required(REQUIRED_FIELD)
      .email(EMAIL_FIELD)
      .transform((value) => value?.toLowerCase().trim())
      .oneOf([yup.ref("email")], "Os e-mails devem ser iguais"),
    password: yup
      .string()
      .required(REQUIRED_FIELD)
      .matches(/^[a-z0-9]+$/i, "Deve conter apenas letras e números")
      .min(8, "Deve conter no mínimos 8 dígitos"),
    passwordConfirm: yup
      .string()
      .required(REQUIRED_FIELD)
      .matches(/^[a-z0-9]+$/i, "Deve conter apenas letras e números")
      .min(8, "Deve conter no mínimos 8 dígitos")
      .oneOf([yup.ref("password")], "As senhas devem ser iguais"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schemaSignup),
  });

  const onSubmitSignup = (data: SignUp) => {
    setLoading(true);
    registerSignup(data);
    generalSubmit();
  };

  const generalSubmit = async () => {
    const formData: RegisterData = getRegisterData();
    const payload: RegisterPayload = {
      user: {
        full_name: formData.owner?.full_name as string,
        email: formData?.signup?.email as string,
        document: formData?.owner?.document as string,
        phone: formData?.carwash?.phone as string,
        password: getValues().password as string,
        receive_communications:
          formData.signup?.receive_communications ?? false,
        privacy_terms: formData.signup?.privacy_terms ?? false,
      },
      carwash: {
        car_wash_name: formData?.carwash?.car_wash_name as string,
        company_name: formData?.carwash?.company_name as string,
        description: "" as string,
        phone: formData?.carwash?.phone as string,
        cnpj: formData?.carwash?.cnpj as string,
        email: formData?.signup?.email as string,
        logo: "",
        category_id: formData?.carwash?.category_id,
        address: {
          street: formData?.address?.street as string,
          number: formData?.address?.number as string,
          complement: formData?.address?.complement as string,
          city: formData?.address?.city as string,
          state: formData?.address?.state as string,
          zip_code: formData?.address?.zip_code as string,
          country: formData?.address?.country as string,
          neighborhood: formData?.address?.neighborhood as string,
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}carwashes/user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setOpen(true);
        setAlert({ type: "error", message: data.message });
        throw new Error("Network response was not ok");
      }

      navigate("/register/confirmation");
      setLoading(false);
    } catch (error) {
      console.error("Erro ao enviar o formulário:", error);
      setLoading(false);
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (!canProceed()) {
      navigate("/parceiros");
      return;
    }

    const { signup } = getRegisterData();
    setValue("email", signup?.email as string);
  }, []);

  return (
    <div className="Register">
      <Container maxWidth="sm" className="MainContainer">
        <RegisterHeader />
        {alert && (
          <Collapse in={open} sx={{ marginTop: "100px" }}>
            <Alert
              variant="filled"
              severity={alert.type}
              onClose={() => setOpen(false)}
            >
              {alert.message}
            </Alert>
          </Collapse>
        )}
        <Typography variant="h1" sx={{ paddingTop: open ? "50px" : "150px" }}>
          Crie seu login
        </Typography>
        <Typography variant="h3" className="Subtitle">
          Preencha com os dados de acesso
        </Typography>

        <form onSubmit={handleSubmit(onSubmitSignup)}>
          <Grid container mt={"50px"} spacing={2}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="email">
                        E-mail que vou acessar o app
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="email"
                        type="text"
                        placeholder="Exemplo: lavarapido@gmail.com"
                        fullWidth
                        error={!!errors.email}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.email?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="emailConfirm"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="emailConfirm">
                        Confirmar e-mail
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="emailConfirm"
                        type="text"
                        placeholder="Preencha como no campo acima"
                        fullWidth
                        error={!!errors.emailConfirm}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.emailConfirm?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="password"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="password">
                        Criar senha de acesso
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Deve conter letras e números"
                        fullWidth
                        error={!!errors.password}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.password?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="passwordConfirm"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="passwordConfirm">
                        Confirmar senha de acesso
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="passwordConfirm"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Preencha como no campo acima"
                        fullWidth
                        error={!!errors.passwordConfirm}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowConfirmPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.passwordConfirm?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                className="BackButton"
                fullWidth
                onClick={() => navigate("/register/carwash")}
                disabled={loading}
              >
                Voltar
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LoadingButton
                type="submit"
                loading={loading}
                loadingIndicator="Enviando dados..."
                variant="contained"
                className="SendButton"
                fullWidth
                onClick={() => {
                  ReactGA.event("continue_signup_click", {
                    event: 'continue_signup_click',
                    button_id: 'continue_signup',
                    page_path: window.location.pathname
                  })
                }}
              >
                <span>Continuar</span>
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
        
        <Box sx={{ textAlign: 'center', marginTop: 4, marginBottom: 2 }}>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: '1rem'
          }}>
            Ficou alguma dúvida?{" "}
            <a 
              href="/fale-conosco" 
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Fale Conosco
            </a>
          </Typography>
        </Box>
      </Container>

      <Footer />
    </div>
  );
};

export default RegisterSignUp;
